import { FunctionComponent, ReactNode, RefObject } from "react";

import AppLogo from "@/assets/images/favicon.png";

import { AiMessage, ChatChip, TypingIndicator, UserMessage } from "./ChatPrimitives";

export interface ChatShellEntry {
	id: number;
	kind: "ai" | "user";
	node: ReactNode;
	chips?: ChatChip[];
}

interface ChatShellProps {
	messages: ChatShellEntry[];
	isTyping: boolean;
	scrollRef: RefObject<HTMLDivElement>;
	/** Hides the composer when the script currently expects a chip, not typing. */
	showInput: boolean;
	placeholder: string;
	value: string;
	onChange: (value: string) => void;
	onSend: () => void;
}

/**
 * The scripted onboarding chat's frame: branded header, the scrollable message
 * column, and the composer the script shows only while it expects free text.
 */
export const ChatShell: FunctionComponent<ChatShellProps> = ({
	messages,
	isTyping,
	scrollRef,
	showInput,
	placeholder,
	value,
	onChange,
	onSend,
}) => (
	<div className="w-full flex flex-col items-center justify-center">
		<div className="flex h-[580px] w-[560px] max-w-[100%] flex-col rounded-[20px] bg-white shadow-[0px_0px_80px_0px_rgba(171,178,217,0.35)]">
			<div className="flex items-center gap-2 border-b border-gray-200 px-6 py-3.5">
				<img alt="logo" className="h-7 w-7" src={AppLogo} />
				<span className="text-sm font-bold">Signalist</span>
			</div>

			<div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5">
				{messages.map((entry) =>
					entry.kind === "ai" ? (
						<AiMessage key={entry.id} chips={entry.chips}>
							{entry.node}
						</AiMessage>
					) : (
						<UserMessage key={entry.id}>{entry.node}</UserMessage>
					)
				)}
				{isTyping && <TypingIndicator />}
			</div>

			{showInput && (
				<div className="flex items-center gap-2 border-t border-gray-200 px-4 py-3">
					<input
						autoFocus
						className="input-styles h-10 flex-1 rounded-lg border px-3 text-ssm"
						placeholder={placeholder}
						value={value}
						onChange={(event) => onChange(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter") {
								onSend();
							}
						}}
					/>
					<button
						className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg bg-blue-action text-lg text-white transition-opacity duration-200 disabled:cursor-not-allowed disabled:opacity-50"
						disabled={!value.trim()}
						type="button"
						onClick={onSend}
					>
						↑
					</button>
				</div>
			)}
		</div>
	</div>
);
